/** All content types supported by the content authority engine */
export const CONTENT_TYPES = [
  'linkedin_post',
  'seo_article',
  'comparison_page',
  'industry_page',
  'use_case_page',
  'faq_page',
  'case_study',
  'glossary_entry',
  'landing_page',
  'integration_page',
  'solution_page',
  'feature_page',
] as const;

export type ContentType = typeof CONTENT_TYPES[number];

/** Content types that represent website pages (eligible for interlinking) */
export const WEBSITE_CONTENT_TYPES = [
  'seo_article',
  'comparison_page',
  'faq_page',
  'industry_page',
  'use_case_page',
  'landing_page',
  'glossary_entry',
  'case_study',
  'integration_page',
  'solution_page',
  'feature_page',
] as const;

export type WebsiteContentType = typeof WEBSITE_CONTENT_TYPES[number];

/** Supported generation languages */
export const CONTENT_LANGUAGES = ['en', 'fr', 'nl', 'de'] as const;

export type ContentLanguage = typeof CONTENT_LANGUAGES[number];

/** ICP target segments aligned with SEQUESTO's market */
export const ICP_SEGMENTS = [
  'pharma_lifesciences',
  'financial_services',
  'critical_infrastructure',
  'professional_services',
  'enterprise_saas',
  'facility_management',
  'defense',
  'general',
] as const;

export type IcpSegment = typeof ICP_SEGMENTS[number];

/** Target buyer roles */
export const ICP_ROLES = [
  'bid_manager',
  'procurement_head',
  'cto',
  'compliance_officer',
  'sales_director',
  'it_director',
  'general',
] as const;

export type IcpRole = typeof ICP_ROLES[number];

/** Buying journey stages */
export const ICP_BUYING_STAGES = [
  'problem_aware',
  'solution_aware',
  'decision_ready',
] as const;

export type IcpBuyingStage = typeof ICP_BUYING_STAGES[number];

/** Account tier classification */
export const ICP_TIERS = ['tier1', 'tier2', 'experimental'] as const;

export type IcpTier = typeof ICP_TIERS[number];

/** Geographic focus areas */
export const GEO_FOCUS_AREAS = ['uki', 'benelux', 'dach', 'nordics', 'france', 'global'] as const;

export type GeoFocus = typeof GEO_FOCUS_AREAS[number];

/** ICP targeting configuration for content generation */
export interface IcpTarget {
  segments?: IcpSegment[] | undefined;
  role?: IcpRole | undefined;
  buyingStage?: IcpBuyingStage | undefined;
  tier?: IcpTier | undefined;
  geoFocus?: GeoFocus | undefined;
}

/** SEO metadata stored alongside content */
export interface SeoMetadata {
  primaryKeyword?: string | undefined;
  secondaryKeywords?: string[] | undefined;
  metaTitle?: string | undefined;
  metaDescription?: string | undefined;
  structuredData?: Record<string, unknown> | undefined;
  internalLinks?: string[] | undefined;
  anchorPhrases?: string[] | undefined;
  urlSlug?: string | undefined;
  headings?: Array<{ level: number; text: string }> | undefined;
  hreflangAlternates?: Array<{ lang: string; slug: string }> | undefined;
}

/** Topic cluster for organizing related content */
export interface TopicCluster {
  id: string;
  name: string;
  slug: string;
  pillarKeyword: string | null;
  description: string | null;
  language: ContentLanguage;
  metadata: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}
